import { type FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  ModalHeader,
  Status,
  TextAreaComponent,
  TextInput,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { useModalManager } from "../../../../context/ModalManager";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  BillingCollectionsCaseApiType,
  CompanySubscriptionDetailsEntry,
} from "../../../../util/types";
// Deep import on purpose: the services package index pulls in server-only
// modules (mongoose, provider SDKs); this file depends on toolkit types only.
import { productScopeForSubscriptionOption } from "@shared_packages/toolkit/services/src/billing/collections-case";

type ActionKind = "start" | "extend" | "release" | "force-restrict";

type Props = {
  companyId: string;
  automatedBillingEnabled: boolean | undefined;
  activeSubscription: CompanySubscriptionDetailsEntry | undefined;
  canManage: boolean;
};

const Section = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Label = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const CaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CaseRow = styled.div`
  border: 1px solid #eeeeee;
  border-radius: 8px;
  background: #fafafa;
  padding: 12px;
`;

const CaseTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const CaseTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProductTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  background: #eef6ff;
  color: #027aff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px 14px;
`;

const MetaItem = styled.div``;

const MetaLabel = styled.div`
  font-size: 10px;
  color: #999;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
`;

const MetaValue = styled.div`
  font-size: 12px;
  color: #222;
  font-weight: 600;
  word-break: break-word;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  padding: 14px;
  color: #888;
  font-size: 13px;
  background: #fff;
`;

const Warning = styled.div`
  border: 1px solid #fcd34d;
  background: #fff8ef;
  color: #7c4a00;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  margin-bottom: 12px;
`;

const Modal = styled.div`
  width: min(520px, 92vw);
  max-height: 90vh;
  overflow: auto;
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NativeInput = styled.input`
  width: 100%;
  height: 36px;
  border: 1px solid #dbe3eb;
  border-radius: 5px;
  padding: 0 12px;
  box-sizing: border-box;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #0e233e;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #13273f;
  margin-bottom: 6px;
`;

const HelpText = styled.div`
  margin-top: 5px;
  font-size: 11px;
  color: #888;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
`;

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function productLabel(scope?: string): string {
  switch (scope) {
    case "payments_only":
      return "Payments Only";
    case "payments_only_eu":
      return "EU Open Banking";
    case "toolkit":
      return "Toolkit";
    case "toolkit_mobile":
      return "Toolkit Mobile";
    case "wallet":
      return "Wallet";
    default:
      return scope || "—";
  }
}

function toIsoDateTime(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : undefined;
}

function actionTitle(action: ActionKind): string {
  switch (action) {
    case "start":
      return "Start collections grace";
    case "extend":
      return "Extend grace";
    case "release":
      return "Release collections case";
    case "force-restrict":
      return "Force restrict";
  }
}

function actionSuccess(action: ActionKind): string {
  switch (action) {
    case "start":
      return "Collections grace started";
    case "extend":
      return "Collections grace extended";
    case "release":
      return "Collections case released";
    case "force-restrict":
      return "Collections case restricted";
  }
}

const CollectionsActionModal: FC<{
  action: ActionKind;
  caseId?: string;
  companyId: string;
  subscriptionId?: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}> = ({ action, caseId, companyId, subscriptionId, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [graceDays, setGraceDays] = useState("14");
  const [graceEndsAt, setGraceEndsAt] = useState("");
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const needsEndDate = action === "extend";
  const canSubmit =
    reason.trim().length > 0 &&
    (action !== "start" || !!subscriptionId) &&
    (!needsEndDate || !!graceEndsAt);

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      if (action === "start") {
        await api.startBillingCollectionsGrace({
          companyId,
          subscriptionId: subscriptionId!,
          reason: reason.trim(),
          graceDays: Number(graceDays) || undefined,
          graceEndsAt: toIsoDateTime(graceEndsAt),
        });
      } else if (action === "extend") {
        await api.extendBillingCollectionsGrace(caseId!, {
          reason: reason.trim(),
          graceEndsAt: toIsoDateTime(graceEndsAt)!,
        });
      } else if (action === "release") {
        await api.releaseBillingCollectionsCase(caseId!, {
          reason: reason.trim(),
        });
      } else {
        await api.forceRestrictBillingCollectionsCase(caseId!, {
          reason: reason.trim(),
        });
      }
      showNotification(actionSuccess(action), NotificationTypes.SUCCESS);
      await onDone();
      onClose();
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Collections action failed"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal>
      <ModalHeader title={actionTitle(action)} handleClose={onClose} />
      <ModalContent>
        <FieldStack>
          <TextAreaComponent
            label="Reason"
            required
            rows="4"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Required support/audit reason"
          />

          {action === "start" && (
            <TextInput
              label="Grace days"
              type="number"
              min={1}
              max={90}
              value={graceDays}
              onChange={(event) => setGraceDays(event.target.value)}
            />
          )}

          {(action === "start" || action === "extend") && (
            <div>
              <FieldLabel>
                Grace end date{action === "extend" ? " *" : ""}
              </FieldLabel>
              <NativeInput
                type="datetime-local"
                value={graceEndsAt}
                onChange={(event) => setGraceEndsAt(event.target.value)}
              />
              {action === "start" && (
                <HelpText>
                  Optional. If empty, backend uses grace days.
                </HelpText>
              )}
            </div>
          )}
        </FieldStack>

        <ModalActions>
          <Button variant={ButtonVariant.OUTLINED} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.CONTAINED}
            onClick={submit}
            disabled={!canSubmit}
            loading={loading}
          >
            Confirm
          </Button>
        </ModalActions>
      </ModalContent>
    </Modal>
  );
};

export const SubscriptionCollectionsCases: FC<Props> = ({
  companyId,
  automatedBillingEnabled,
  activeSubscription,
  canManage,
}) => {
  const [cases, setCases] = useState<BillingCollectionsCaseApiType[]>([]);
  const [loading, setLoading] = useState(false);
  const { addModal, removeLastModal } = useModalManager();
  const { showNotification } = useNotification();

  const subscriptionId = activeSubscription?.subscriptionId;
  // Same option → product-scope mapping the backend uses for restrictions, so
  // dual subscriptions keep independent grace/restriction states and the UI
  // can't drift from the products collections actually supports.
  const productScope = productScopeForSubscriptionOption(
    activeSubscription?.subscriptionOption
  );

  const loadCases = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const result = await api.getBillingCollectionsCases({
        companyId,
        subjectType: "subscription",
        limit: 50,
      });
      setCases(result.items ?? []);
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Could not load collections cases"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, [companyId]);

  const visibleCases = useMemo(() => {
    if (subscriptionId) {
      return cases.filter((item) => item.subjectId === subscriptionId);
    }
    // Older/partial company payloads can miss subscriptionId; product scope
    // still lets support see the relevant case instead of an empty panel.
    if (productScope) {
      return cases.filter((item) => item.productScope === productScope);
    }
    return [];
  }, [cases, productScope, subscriptionId]);

  const activeCase = visibleCases.find(
    (item) => item.status === "grace" || item.status === "restricted"
  );
  const canStart =
    canManage &&
    automatedBillingEnabled === true &&
    !!subscriptionId &&
    !!productScope &&
    !activeCase;
  const startDisabledReason = !canManage
    ? "You do not have permission to manage company billing actions."
    : automatedBillingEnabled !== true
    ? "Automated billing is disabled for this company."
    : !productScope
    ? "This subscription product is not supported by collections."
    : !subscriptionId
    ? "Subscription id is missing from the billing payload."
    : activeCase
    ? "This subscription already has an active collections case."
    : null;

  const openAction = (
    action: ActionKind,
    item?: BillingCollectionsCaseApiType
  ) => {
    addModal(
      <CollectionsActionModal
        action={action}
        caseId={item?._id}
        companyId={companyId}
        subscriptionId={subscriptionId}
        onClose={removeLastModal}
        onDone={loadCases}
      />
    );
  };

  return (
    <Section>
      <Header>
        <div>
          <Label>Collections</Label>
          <Typography variant={TypographyVariant.BODY2}>
            Product-scoped grace and restriction state for this subscription.
          </Typography>
        </div>
        <Button
          variant={ButtonVariant.OUTLINED}
          onClick={() => openAction("start")}
          disabled={!canStart}
        >
          Start grace
        </Button>
      </Header>

      {startDisabledReason && <Warning>{startDisabledReason}</Warning>}

      {loading && <EmptyState>Loading collections cases...</EmptyState>}

      {!loading && visibleCases.length === 0 && (
        <EmptyState>No collections cases for this subscription.</EmptyState>
      )}

      {!loading && visibleCases.length > 0 && (
        <CaseList>
          {visibleCases.map((item) => {
            const latestAdminEvent =
              item.adminEvents?.[item.adminEvents.length - 1];
            const canRelease =
              canManage &&
              (item.status === "grace" || item.status === "restricted");
            const canExtend = canManage && item.status === "grace";
            const canForceRestrict = canManage && item.status === "grace";

            return (
              <CaseRow key={item._id}>
                <CaseTop>
                  <CaseTitle>
                    <Status status={item.status} />
                    <ProductTag>{productLabel(item.productScope)}</ProductTag>
                  </CaseTitle>
                  <MetaValue>{item._id}</MetaValue>
                </CaseTop>

                <MetaGrid>
                  <MetaItem>
                    <MetaLabel>Trigger</MetaLabel>
                    <MetaValue>{item.trigger.replaceAll("_", " ")}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Grace started</MetaLabel>
                    <MetaValue>{formatDate(item.graceStartedAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Grace ends</MetaLabel>
                    <MetaValue>{formatDate(item.graceEndsAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Restricted at</MetaLabel>
                    <MetaValue>{formatDate(item.restrictedAt)}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Invoices</MetaLabel>
                    <MetaValue>{item.invoiceIds.length}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Last admin reason</MetaLabel>
                    <MetaValue>{latestAdminEvent?.reason ?? "—"}</MetaValue>
                  </MetaItem>
                </MetaGrid>

                <ActionBar>
                  <Button
                    variant={ButtonVariant.OUTLINED}
                    onClick={() => openAction("extend", item)}
                    disabled={!canExtend}
                  >
                    Extend grace
                  </Button>
                  <Button
                    variant={ButtonVariant.OUTLINED}
                    onClick={() => openAction("release", item)}
                    disabled={!canRelease}
                  >
                    Release
                  </Button>
                  <Button
                    variant={ButtonVariant.OUTLINED}
                    onClick={() => openAction("force-restrict", item)}
                    disabled={!canForceRestrict}
                  >
                    Force restrict
                  </Button>
                </ActionBar>
              </CaseRow>
            );
          })}
        </CaseList>
      )}
    </Section>
  );
};
